import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    forwardRef,
    Input,
    ViewChild,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NgForm,
} from '@angular/forms';
import { map, take, tap } from 'rxjs/operators';
import { BusinessOwnerService } from 'src/app/acl-data/business-owner/business-owner.service';
import {
    Document,
    INITIAL_DOCUMENT,
} from 'src/app/acl-data/document/document.dto';
import { DocumentService } from 'src/app/acl-data/document/document.service';
import { Folder } from 'src/app/acl-data/folder/folder.dto';
import { LegalCounselService } from 'src/app/acl-data/legal-counsel/legal-counsel.service';
import { ObjectStoreService } from 'src/app/acl-data/object-store/object-store.service';
import { UserDataService } from 'src/app/acl-data/userData/user-data.service';

export enum DocumentClasses {
    Fixed_Term_Contract = 'FixedTermContract',
    Indefinete_Contract = 'Contract',
    Transaction_Contract = 'TransactionContract',
}

@Component({
    templateUrl: './document-properties-edit.component.html',
    styleUrls: ['./document-properties-edit.component.scss'],
    selector: 'document-properties-edit',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DocumentPropertiesEditComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentPropertiesEditComponent implements ControlValueAccessor, AfterViewChecked {
    DocumentClasses = DocumentClasses;
    @Input() folder: Folder;
    @Input() document: Document
    @Input() objectStore: string;
    @Input() masterDataRequired: boolean;
    @Input() addMode = false;

    showError = false;

    @ViewChild('documentForm', { static: true }) documentForm: NgForm;

    userRoles$ = this.objectStoreService.entities$.pipe(
        map(v => v.find(row => row.symbolicName === this.objectStore)));

    initiator$ = this.userService.userData$.pipe(map(v => v.fullname)).pipe(map(
        x=>{
            const index = this.document && this.document.acl ? this.document.acl.findIndex(y => y.name == x && y.level == 'Initiator') : -1
        if(index !== -1){
            return false
        }else{
            return true
        }
        }
    ))
        
    

    get isValid(): boolean {
        return this.documentForm && this.documentForm.valid;
    }

    tomorrow = new Date(new Date().setDate(new Date().getDate() + 1));

    doc: Document = INITIAL_DOCUMENT;

    onChanges = [];
    onTouched = [];
    disabled = false;
    boSearch = '';
    lcSearch = '';
    lcSelection = '';

    ngAfterViewChecked() {

        this.initiator$.subscribe(x=> console.log)
        //This removes the legal counsel from this.doc.propertiesObj so that the form is invalid when no lc is selected
        if (!!this.doc.propertiesObj.ContractLegalCounsel){
            this.legalCounselService.getByKey(this.doc.propertiesObj.ContractLegalCounsel).subscribe(lc => {
                if(!!lc && lc.legalCounselPk == this.doc.propertiesObj.ContractLegalCounsel){
                    this.lcSelection = this.doc.propertiesObj.ContractLegalCounsel
                }
                if (this.lcSelection == ''){
                    delete this.doc.propertiesObj.ContractLegalCounsel
                }
                
                //console.log("Legal counsel selection: " + this.lcSelection)
            })
        }
    }


    public focusOnError() {
        // console.log('Focus on error', this.documentForm);
        this.showError = true;

        const invalidElements = this.el.nativeElement.querySelectorAll(
            '.ng-invalid'
        );
        if (invalidElements.length > 0) {
            // console.log(invalidElements[0]);

            invalidElements[0].focus();
            invalidElements[0].scrollIntoView();
        }
    }

    showOption = input => {
        if (!this.folder || !this.folder.propertiesObj.FolderFilterClasses) {
            return true;
        } else {
            return (
                this.folder.propertiesObj.FolderFilterClasses.indexOf(input) >=
                0
            );
        }
    };

    constructor(
        private businessOwnerService: BusinessOwnerService,
        private legalCounselService: LegalCounselService,
        private objectStoreService: ObjectStoreService,
        private el: ElementRef,
        private userService: UserDataService,
        private documentService: DocumentService
    ) { }

    private setOnChangeCallback(fn: any) {
        this.onChanges.push(fn);
    }

    private setOnTouchCallback(fn: any) {
        this.onTouched.push(fn);
    }

    setIsDisabled(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    writeValue(obj: any): void {
        if (!!obj) {
            this.doc = obj;
        } else {
            this.doc = INITIAL_DOCUMENT;
        }
    }
    registerOnChange(fn: any): void {
        // console.log('registerOnChange');
        this.setOnChangeCallback(fn);
    }
    registerOnTouched(fn: any): void {
        this.setOnTouchCallback(fn);
    }
    setDisabledState?(isDisabled: boolean): void {
        this.setIsDisabled(isDisabled);
    }

    formChanged = (input: any) => {
        for (const change of this.onChanges) {
            change(this.doc);
        }
    };

    formTouched = (input: any) => {
        console.log('Form has been touched');
        for (const touch of this.onTouched) {
            touch(input);
        }
    };

    public touchAll() {
        for (const ctrKey of Object.keys(this.documentForm.controls)) {
            // console.log(ctrKey, this.documentForm.controls[ctrKey]);
            this.documentForm.controls[ctrKey].markAsTouched();
        }
    }

    boSelected = input => {
        if (
            !this.doc.propertiesObj.ContractDepartment ||
            this.doc.propertiesObj.ContractDepartment.length === 0
        ) {
            this.businessOwnerService
                .getByKey(this.doc.propertiesObj.ContractBusinessOwner)
                .pipe(
                    // tap(v => console.log('BO', v)),
                    take(1)
                )
                .subscribe(v => {
                    if (!v) {
                        console.error(
                            'Could not find Business Owner:',
                            this.doc.propertiesObj.ContractBusinessOwner,
                            this.doc
                        );
                    } else {
                        this.doc.propertiesObj.ContractDepartment =
                            v.department;
                    }
                });
        }
    };

    lcSelected = input => {
        this.lcSelection = input.selected
        this.legalCounselService
            .getByKey(this.doc.propertiesObj.ContractLegalCounsel)
            /*.pipe(
                tap(v => console.log('lcSelected() > Legal Counsel', v)),
                take(1)
            )*/
            .subscribe(v => {
                if (!v) {
                    console.error(
                        'Could not find Legal Counsel:',
                        this.doc.propertiesObj.ContractLegalCounsel,
                        this.doc
                    );
                } else {
                    this.doc.propertiesObj.ContractLegalCounselEmail =
                        v.email;
                }
            });
    };
}
